/**
 * Which device a wheel event came from. Browsers report a trackpad's pinch,
 * its two-finger scroll and a mouse's wheel all as wheel events, and the views
 * treat them differently: a pinch zooms, a trackpad scroll moves (pans or
 * orbits), and a mouse wheel zooms, as it does in other 3D and plan programs.
 */
import type { WheelGesture } from './zoomPan';

export type WheelDevice = 'pinch' | 'trackpad' | 'mouse';

/** The wheel event fields the classifier reads; `wheelDeltaX`/`wheelDeltaY` are the legacy fields Chrome, Safari and Firefox still fill in. */
export interface WheelSample extends WheelGesture {
  wheelDeltaX?: number;
  wheelDeltaY?: number;
  /** Event time (ms), to tell one scroll from the next. */
  timeStamp: number;
}

/** Events closer together than this (ms) belong to one scroll, so momentum and acceleration can't flip its device midway. */
export const WHEEL_STREAM_GAP = 300;

/**
 * A single event's device, with no history. A pinch arrives with ctrlKey
 * set. A trackpad reports pixel deltas whose legacy wheelDelta is exactly
 * −3× the delta; a mouse wheel reports lines (Firefox) or notches of 120
 * in wheelDelta that don't fit that rule. Without legacy fields, a sideways
 * delta or a small one is a trackpad.
 */
export function wheelEventDevice(e: WheelSample): WheelDevice {
  if (e.ctrlKey) return 'pinch';
  if (e.deltaMode) return 'mouse';
  if (e.wheelDeltaY !== undefined && e.wheelDeltaX !== undefined && (e.wheelDeltaX || e.wheelDeltaY)) {
    return e.wheelDeltaX === -3 * e.deltaX && e.wheelDeltaY === -3 * e.deltaY ? 'trackpad' : 'mouse';
  }
  return e.deltaX || Math.abs(e.deltaY) < 50 ? 'trackpad' : 'mouse';
}

/**
 * A classifier for one view's wheel events. Each scroll keeps the device its
 * first event showed until it pauses for WHEEL_STREAM_GAP; a pinch is always
 * a pinch.
 */
export function createWheelClassifier(): (e: WheelSample) => WheelDevice {
  let stream: { device: Exclude<WheelDevice, 'pinch'>; at: number } | null = null;
  return e => {
    const device = wheelEventDevice(e);
    if (device === 'pinch') return device;
    if (!stream || e.timeStamp - stream.at > WHEEL_STREAM_GAP) stream = { device, at: e.timeStamp };
    stream.at = e.timeStamp;
    return stream.device;
  };
}

/** One mouse-wheel step's zoom factor. */
export const MOUSE_WHEEL_ZOOM_STEP = 1.2;

/** The pixels in one wheel notch where the browser reports a large delta (Windows, Linux). */
const NOTCH_PIXELS = 100;

/**
 * The zoom factor for one mouse-wheel event: a step per notch, more for a
 * fast spin the browser folded into one event, in for a wheel rolled away
 * from the user.
 */
export function mouseWheelZoomFactor(e: WheelGesture): number {
  const delta = e.deltaMode ? e.deltaY * 16 : e.deltaY;
  if (!delta) return 1;
  const notches = Math.min(4, Math.max(1, Math.round(Math.abs(delta) / NOTCH_PIXELS)));
  return MOUSE_WHEEL_ZOOM_STEP ** (delta < 0 ? notches : -notches);
}
